/**
 * API Service for communication with the roamzap backend
 */
import { getAuthToken } from '../firebase/firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Generate an itinerary using the backend API
 * @param {Object} tripDetails - The trip details
 * @returns {Promise<Object>} - The generated itinerary
 */
export const generateItineraryAPI = async (tripDetails) => {
  try {
    // Get auth token for authenticated requests
    const token = getAuthToken();
    
    // Transform frontend data structure to match backend expectations
    const apiPayload = {
      destination: tripDetails.destination,
      startDate: formatDate(tripDetails.startDate),
      endDate: formatDate(tripDetails.endDate),
      travelers: (tripDetails.adults || 1) + (tripDetails.children || 0),
      children: tripDetails.children || 0,
      budget: tripDetails.budget || 'medium',
      interests: formatInterests(tripDetails.interests)
    };
    
    // If this is a regeneration, include the itineraryId
    if (tripDetails.itineraryId) {
      apiPayload.itineraryId = tripDetails.itineraryId;
    }
    
    // Prepare headers with authentication if available
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Add authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Use correct endpoint path (no double /api)
    const endpoint = `${API_BASE_URL}/itineraries`;
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(apiPayload),
    });
    
    // Check if the response is OK
    if (!response.ok) {
      const errorData = await response.json();
      
      // Handle token-specific errors
      if (response.status === 402 && errorData.tokenRequired) {
        throw new Error('Insufficient tokens to generate a new itinerary');
      }
      
      // Handle destination change error
      if (response.status === 400 && errorData.message?.includes('Destination cannot be changed')) {
        throw new Error('Destination cannot be changed for security reasons. Please create a new itinerary instead.');
      }
      
      throw new Error(errorData.message || 'Failed to generate itinerary');
    }
    
    // Parse the response
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Failed to generate itinerary');
    }
    
    // Transform the backend response to match frontend expectations
    return transformApiResponse(data.data, tripDetails, {
      tokenBalance: data.tokenBalance,
      isRegeneration: data.isRegeneration
    });
  } catch (error) {
    console.error('Error in generateItineraryAPI:', error);
    throw error;
  }
};

/**
 * Format a date object to YYYY-MM-DD string
 * @param {Date} date - The date to format
 * @returns {string} - The formatted date
 */
const formatDate = (date) => {
  if (!date) return '';
  
  // If it's already a string in YYYY-MM-DD format, return as is
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }
  
  // Convert to Date if it's not already
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    throw new Error('Invalid date');
  }
  
  // Format as YYYY-MM-DD using local date to prevent timezone issues
  const year = dateObj.getFullYear();
  // Month is 0-indexed, so add 1 and pad with leading zero if needed
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  // Day of month, padded with leading zero if needed
  const day = String(dateObj.getDate()).padStart(2, '0');
  
  // Return in YYYY-MM-DD format
  return `${year}-${month}-${day}`;
};

/**
 * Format interests to match backend expectations
 * @param {string|Array} interests - The interests as string or array
 * @returns {Array} - Formatted interests array
 */
const formatInterests = (interests) => {
  if (!interests) return [];
  
  // If it's already an array, return it
  if (Array.isArray(interests)) {
    return interests;
  }
  
  // If it's a string, split it by commas
  return interests.split(',').map(i => i.trim()).filter(i => i !== '');
};

/**
 * Transform the API response to match frontend expectations
 * @param {Object} apiResponse - The response from the API
 * @param {Object} originalDetails - The original trip details
 * @param {Object} additionalData - Additional data from the API
 * @returns {Object} - Transformed response
 */
const transformApiResponse = (apiResponse, originalDetails, additionalData) => {
  
  // Convert date strings to Date objects with proper local dates
  const normalizeDate = (dateStr) => {
    if (!dateStr) return new Date();
    
    // If already a string in YYYY-MM-DD format, extract parts and create local date
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(part => parseInt(part, 10));
      // Note: month is 0-indexed in JS Date
      return new Date(year, month - 1, day);
    }
    
    // Otherwise try regular Date constructor
    return new Date(dateStr);
  };
  
  // Structure expected by the frontend
  const transformedResponse = {
    destination: apiResponse.destination,
    startDate: normalizeDate(apiResponse.startDate),
    endDate: normalizeDate(apiResponse.endDate),
    travelers: apiResponse.travelers,
    adults: originalDetails.adults || 1,
    children: originalDetails.children || 0,
    budget: apiResponse.budget,
    interests: apiResponse.interests,
    title: `Your ${apiResponse.days.length}-Day Adventure in ${apiResponse.destination}`,
    summary: apiResponse.summary || `Explore the best of ${apiResponse.destination} with this personalized itinerary.`,
    // Include travelInfo if present
    travelInfo: apiResponse.travelInfo || null,
    // Transform days format if needed
    days: apiResponse.days.map(day => ({
      day: day.dayNumber,
      date: normalizeDate(day.date), // Use normalized date
      activities: day.activities.map(activity => ({
        time: activity.time,
        title: activity.title,
        description: activity.description,
        type: activity.type,
        duration: activity.duration,
        cost: activity.cost,
        bookingLink: activity.bookingLink,
        booking_link: activity.bookißng_link,
        image: activity.image,
        placeDetails: activity.placeDetails || null, // Include placeDetails if present
        location: activity.location || null // Include location if present
      }))
    })),
    // Use API-provided tips or fallback to generic ones
    tips: apiResponse.tips || [
      "Always carry a map or use a reliable map app",
      "Check the weather forecast daily and dress accordingly",
      "Keep important documents and valuables secure",
      "Try the local cuisine for an authentic experience",
      "Learn a few basic phrases in the local language"
    ],
    // Add a flag to indicate this came from the API
    isApiGenerated: true,
    tokenBalance: additionalData.tokenBalance,
    isRegeneration: additionalData.isRegeneration
  };
  return transformedResponse;
}; 